import { LegalPage, type LegalSection } from "#/components/legal/LegalPage.js";

type Props = { locale: string };

const TITLE: Record<string, string> = {
  en: "Privacy",
  ar: "الخصوصية",
  fr: "Confidentialité",
  "zh-CN": "Privacy",
  es: "Privacidad",
  de: "Datenschutz",
  ru: "Privacy",
  hi: "Privacy",
  id: "Privasi",
  ms: "Privasi",
};

const INTRO: Record<string, string> = {
  en: "Hantavirus is a public outbreak tracker. You can read the dashboard, maps, FAQs, and public documentation without creating an account.",
  ar: "هانتا هو متتبع عام لتفشي فيروس هانتا. يمكنك قراءة لوحة المعلومات والخرائط والأسئلة الشائعة والوثائق العامة دون إنشاء حساب.",
  fr: "Hantavirus est un suivi public des épidémies. Vous pouvez consulter le tableau de bord, les cartes, la FAQ et la documentation publique sans créer de compte.",
};

const UPDATED_LABEL: Record<string, string> = {
  en: "Last updated",
  ar: "آخر تحديث",
  fr: "Dernière mise à jour",
};

const SECTIONS: LegalSection[] = [
  {
    heading: "What we collect",
    items: [
      "Public visitors do not need an account. We do not ask for names, passwords, or health records to view outbreak information.",
      "If you subscribe to email updates, we store the email address you submit, your selected locale, subscription preferences, confirmation status, and unsubscribe token.",
      "If you enable push notifications, your browser or device provides a push subscription endpoint and cryptographic keys so alerts can be delivered.",
      "For site reliability and abuse prevention, servers may record ordinary request metadata such as URL, timestamp, IP address, user agent, response status, and error details.",
    ],
  },
  {
    heading: "Cookies, local storage, and PWA data",
    items: [
      "We use a locale cookie and local storage value to remember your language choice.",
      "The PWA may cache public pages and outbreak data on your device so the app can open faster and show offline fallback content.",
      "We do not use tracking pixels on public digest emails, and the public site is designed without behavioral advertising profiles.",
    ],
  },
  {
    heading: "How we use data",
    items: [
      "We use subscription data to send requested outbreak digests, alerts, and unsubscribe or preference-management links.",
      "We use operational logs and aggregate analytics to detect outages, diagnose errors, measure public-page performance, and protect the service from abuse.",
      "We use language and PWA state only to provide the localized and installable experience you selected.",
    ],
  },
  {
    heading: "Sources and third parties",
    body: "The outbreak content shown on the site is aggregated from public-health and public-interest sources such as WHO, CDC, PAHO, ProMED, news feeds, and maritime data providers. Links to those sources may take you to third-party sites with their own privacy practices.",
  },
  {
    heading: "Retention and deletion",
    items: [
      "Email subscription records are kept until you unsubscribe or ask us to delete them.",
      "Operational logs are retained only as long as needed for security, debugging, analytics, and legal compliance.",
      "You can unsubscribe using the link in any digest email. For erasure requests, email privacy@hantavirus.app.",
    ],
  },
  {
    heading: "Security",
    body: "We use ordinary technical safeguards for a public-health information service, including transport encryption, access controls, and least-privilege handling for subscription and operational data. No internet service can guarantee perfect security.",
  },
  {
    heading: "Children",
    body: "The site is intended for general public-health awareness and is not directed at children. We do not knowingly collect personal information from children.",
  },
  {
    heading: "Contact",
    body: "For privacy questions, data deletion, or subscription issues, contact privacy@hantavirus.app and include the email address or device context involved so we can locate the record.",
  },
];

export { Page };

function Page({ locale }: Props) {
  return (
    <LegalPage
      title={TITLE[locale] ?? "Privacy"}
      intro={
        INTRO[locale] ??
        "Hantavirus is a public outbreak tracker. You can read the dashboard, maps, FAQs, and public documentation without creating an account."
      }
      updatedLabel={UPDATED_LABEL[locale] ?? "Last updated"}
      updatedValue="13 May 2026"
      sections={SECTIONS}
    />
  );
}
